<script setup lang="ts">
import InvoiceList from '@/components/invoices/InvoiceList.vue';
import QuoteList from '@/components/quotes/QuoteList.vue';
import { Button } from '@/components/ui/button';
import SectionSurface from '@/components/workspace/SectionSurface.vue';
import MetricCard from '@/components/workspace/MetricCard.vue';
import { AlertTriangle, FileText, Receipt } from 'lucide-vue-next';
import type { Client, Invoice, Project, Quote } from '~/types/models';
import type {
    ClientFinanceFormatDate,
    ClientFinanceFormatMoney,
    ClientFinanceFormatValue,
    ClientFinanceOption,
    ClientFinanceProjectOption,
} from './types';

interface Props {
    canMutate: boolean;
    linkedClient: Client | null;
    createQuoteOpen: boolean;
    createInvoiceOpen: boolean;
    preparingQuoteClient: boolean;
    financeClientOptions: ClientFinanceOption[];
    financeProjectOptions: ClientFinanceProjectOption[];
    relatedQuotes: Quote[];
    relatedInvoices: Invoice[];
    openQuotes: Quote[];
    outstandingInvoices: Invoice[];
    clientCurrency: string;
    outstandingThisYear: number;
    revenueCollectedThisYear: number;
    currentYear: number;
    formatMoney: ClientFinanceFormatMoney;
    formatHumanValue: ClientFinanceFormatValue;
    formatDate: ClientFinanceFormatDate;
}

const props = defineProps<Props>();
const { tenantPath } = useTenantPath();
const emit = defineEmits<{
    (event: 'update:create-quote-open', value: boolean): void;
    (event: 'update:create-invoice-open', value: boolean): void;
    (event: 'open-create-quote'): void;
    (event: 'created-quote'): void;
    (event: 'created-invoice'): void;
    (event: 'invoice-send', id: string, deliveryMethod?: 'both' | 'portal'): void;
    (event: 'invoice-remind', id: string): void;
    (event: 'invoice-mark-paid', id: string): void;
    (event: 'invoice-copy-public-link', invoice: Invoice): void;
    (event: 'quote-send', id: string): void;
    (event: 'quote-remind', id: string): void;
    (event: 'quote-accept', id: string): void;
    (event: 'quote-approve-internally', id: string): void;
    (event: 'quote-reject', id: string): void;
    (event: 'quote-create-full-invoice', id: string): void;
    (event: 'quote-create-deposit-invoice', id: string): void;
    (event: 'quote-create-balance-invoice', id: string): void;
    (event: 'quote-create-next-installment-invoice', id: string): void;
    (event: 'quote-copy-public-link', quote: Quote): void;
}>();

const projectNameById = computed(() => Object.fromEntries(props.financeProjectOptions.map(project => [project.id, project.name])));
const quoteNumberById = computed(() => Object.fromEntries(props.relatedQuotes.map(quote => [quote.id, quote.quoteNumber || ''])));
const quoteInvoicesByQuoteId = computed(() => props.relatedInvoices.reduce((grouped, invoice) => {
    if (invoice.quoteId) (grouped[invoice.quoteId] ||= []).push(invoice);
    return grouped;
}, {} as Record<string, Invoice[]>));

function openDocument(type: 'quotes' | 'invoices', document: Quote | Invoice, edit = false) {
    void navigateTo(tenantPath({ path: `/${type}/${document.id}`, query: edit ? { edit: '1' } : undefined }));
}

function createDocument(type: 'quotes' | 'invoices', clientId?: string) {
    if (!clientId) return;
    void navigateTo(tenantPath({ path: `/${type}/new`, query: { clientId } }));
}
</script>

<template>
    <div class="space-y-6">
        <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <MetricCard density="compact" label="Open quotes" :value="openQuotes.length" description="Quotes still in progress" :icon="FileText" :href="tenantPath({ path: '/quotes', query: { clientId: linkedClient?.id } })" />
            <MetricCard density="compact" label="Invoices" :value="relatedInvoices.length" description="All client invoices" :icon="Receipt" :href="tenantPath({ path: '/invoices', query: { clientId: linkedClient?.id } })" />
            <MetricCard density="compact" label="Collected this year" :value="formatMoney(revenueCollectedThisYear, clientCurrency)" description="Payments received this year" :icon="Receipt" :href="tenantPath({ path: '/invoices', query: { clientId: linkedClient?.id, status: 'paid' } })" />
            <MetricCard density="compact" label="Outstanding" :value="formatMoney(outstandingThisYear, clientCurrency)" description="Payment still due" :icon="AlertTriangle" :href="tenantPath({ path: '/invoices', query: { clientId: linkedClient?.id, status: 'outstanding' } })" />
        </div>

        <div class="space-y-8">
            <SectionSurface eyebrow="Commercial paper" title="Quotes" description="Proposal and pricing drafts for this client account." :icon="FileText" class="h-fit" presentation="editorial">
                <template #actions>
                    <Button v-if="canMutate" variant="outline" size="sm" class="rounded-full" :disabled="!linkedClient" @click="createDocument('quotes', linkedClient?.id)"><FileText class="mr-2 size-4" />Create quote</Button>
                </template>

                <div class="divide-y divide-border/60 rounded-lg border border-border/60 sm:grid sm:grid-cols-2 sm:divide-x sm:divide-y-0">
                    <div class="p-4"><p class="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Live quote value</p><p class="mt-2 text-2xl font-semibold text-foreground">{{ formatMoney(openQuotes.reduce((sum, quote) => sum + Number(quote.amountTotal || 0), 0), clientCurrency) }}</p><p class="mt-1 text-xs text-muted-foreground">{{ openQuotes.length ? `${openQuotes.length} active quote${openQuotes.length === 1 ? '' : 's'}` : 'No active quotes right now.' }}</p></div>
                    <div class="p-4"><p class="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Client readiness</p><p class="mt-2 text-sm font-semibold text-foreground">{{ linkedClient ? 'Ready to send when approved' : 'Client required' }}</p><p class="mt-1 text-xs text-muted-foreground">{{ linkedClient ? 'Quotes are ready to move forward.' : 'Create or link the client before sending a quote.' }}</p></div>
                </div>
                <div class="mt-4">
                    <QuoteList
                        :quotes="relatedQuotes"
                        :show-client="false"
                        :project-name-by-id="projectNameById"
                        :quote-invoices-by-quote-id="quoteInvoicesByQuoteId"
                        :can-edit="canMutate"
                        :can-send="canMutate"
                        :can-accept="canMutate"
                        :can-reject="canMutate"
                        :can-generate-invoices="canMutate"
                        :can-copy-public-link="canMutate"
                        empty-title="No quotes yet"
                        empty-description="Create the first quote for this client account."
                        @view="openDocument('quotes', $event)"
                        @edit="openDocument('quotes', $event, true)"
                        @view-linked-invoice="openDocument('invoices', $event)"
                        @send="emit('quote-send', $event)"
                        @remind="emit('quote-remind', $event)"
                        @accept="emit('quote-accept', $event)"
                        @approve-internally="emit('quote-approve-internally', $event)"
                        @reject="emit('quote-reject', $event)"
                        @create-full-invoice="emit('quote-create-full-invoice', $event)"
                        @create-deposit-invoice="emit('quote-create-deposit-invoice', $event)"
                        @create-balance-invoice="emit('quote-create-balance-invoice', $event)"
                        @create-next-installment-invoice="emit('quote-create-next-installment-invoice', $event)"
                        @copy-public-link="emit('quote-copy-public-link', $event)"
                    />
                </div>
            </SectionSurface>

            <SectionSurface eyebrow="Cashflow" title="Invoices" description="Outstanding and collected value tied to this client account." :icon="Receipt" class="h-fit" presentation="editorial">
                <template #actions>
                    <Button v-if="canMutate" variant="outline" size="sm" class="rounded-full" :disabled="!linkedClient" @click="createDocument('invoices', linkedClient?.id)"><Receipt class="mr-2 size-4" />Create invoice</Button>
                </template>

                <div v-if="!linkedClient" class="mb-4 rounded-lg border border-dashed border-border/70 bg-background/55 px-4 py-3 text-sm text-muted-foreground">Choose a client before creating an invoice.</div>
                <div class="divide-y divide-border/60 rounded-lg border border-border/60 sm:grid sm:grid-cols-2 sm:divide-x sm:divide-y-0">
                    <div class="p-4"><p class="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Outstanding balance</p><p class="mt-2 text-2xl font-semibold text-foreground">{{ formatMoney(outstandingInvoices.reduce((sum, invoice) => sum + Number(invoice.amountDue || 0), 0), outstandingInvoices[0]?.currency || clientCurrency) }}</p><p class="mt-1 text-xs text-muted-foreground">{{ outstandingInvoices.length ? `${outstandingInvoices.length} invoice${outstandingInvoices.length === 1 ? '' : 's'} still waiting for payment.` : 'Nothing is currently outstanding.' }}</p></div>
                    <div class="p-4"><p class="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Collected this year</p><p class="mt-2 text-2xl font-semibold text-foreground">{{ formatMoney(revenueCollectedThisYear, clientCurrency) }}</p><p class="mt-1 text-xs text-muted-foreground">Payments booked across {{ currentYear }}.</p></div>
                </div>
                <div class="mt-4">
                    <InvoiceList
                        :invoices="relatedInvoices"
                        :show-client="false"
                        :project-name-by-id="projectNameById"
                        :quote-number-by-id="quoteNumberById"
                        :can-edit="canMutate"
                        :can-send="canMutate"
                        :can-mark-paid="canMutate"
                        :can-copy-public-link="canMutate"
                        empty-title="No invoices yet"
                        empty-description="Invoices created here will keep the financial story connected to the client account."
                        @view="openDocument('invoices', $event)"
                        @edit="openDocument('invoices', $event, true)"
                        @send="(id, method) => emit('invoice-send', id, method)"
                        @remind="emit('invoice-remind', $event)"
                        @mark-paid="emit('invoice-mark-paid', $event)"
                        @copy-public-link="emit('invoice-copy-public-link', $event)"
                    />
                </div>
            </SectionSurface>
        </div>
    </div>
</template>
